import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { gql, useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { toastHandler } from "../../../../../utils/helpers";
import { toastTypesKeys } from "../../../../../utils/constants";
import { ToastContainer } from "react-toastify";

const CREATE_GEOGRAPHICAL_AREA = gql`
  mutation CreateGeographicalArea($input: GeographicalAreaCreateInput!) {
    geographicalAreaCreate(input: $input) {
      clientMutationId
      geographicalArea {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_GEOGRAPHICAL_AREA = gql`
  query GetGeographicalArea($id: ID!) {
    geographicalArea(id: $id) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_GEOGRAPHICAL_AREA = gql`
  mutation UpdateGeographicalArea($id: ID!, $name: String!) {
    geographicalAreaUpdate(
      input: { id: $id, geographicalAreaInput: { name: $name } }
    ) {
      clientMutationId
      geographicalArea {
        createdAt
        id
        name
        updatedAt
      }
    }
  }
`;

function GeographicalAction({ isUpdate = false }) {
  const { user } = useAuthSession() as any;
  const notify = useNotify();
  const navigate = useNavigate();
  const { translator, currentLanguage } = useLanguageTranslator();

  const [searchParams] = useSearchParams();
  const geographical_area_id = searchParams.get("id");

  const { data: geographicalareaData } = useQuery(GET_GEOGRAPHICAL_AREA, {
    variables: { id: geographical_area_id },
    skip: !geographical_area_id,
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [createGeographicalArea, { loading, error }] = useMutation(
    CREATE_GEOGRAPHICAL_AREA,
    {
      context: {
        headers: {
          "accept-language": currentLanguage || "en",
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const [updateGeographicalArea] = useMutation(UPDATE_GEOGRAPHICAL_AREA, {
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!geographicalareaData?.geographicalArea) return undefined;

    const { name } = geographicalareaData?.geographicalArea;

    return {
      geographical_area_name: name,
    };
  }, [geographicalareaData]);

  const form = useForm<any>({
    defaultValues: {
      geographical_area_name: "",
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updateGeographicalArea({
          variables: {
            id: Number(geographical_area_id),
            name: data?.geographical_area_name,
          },
        });
        if (response.data) {
          notify.success(`Geographical Area Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/geographical-area");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }

    try {
      const response = await createGeographicalArea({
        variables: {
          input: {
            geographicalAreaInput: { name: data?.geographical_area_name },
          },
        },
      });
      if (actionType === "save") {
        if (response.data) {
          notify.success(`Geographical Area Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/geographical-area");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`Geographical Area Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  }, []);

  return (
    <>
      <ToastContainer />
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <FormControl required disabled={false} fullWidth margin="normal">
              <FormLabel>
                {translator("Geographical Area")}{" "}
                {translator("dashboardTableHead.Name")}
              </FormLabel>
              <OutlinedInput
                {...form?.register("geographical_area_name", {
                  required: "Required!",
                })}
              />
              {/* <FormHelperText>{errors.title?.message}</FormHelperText> */}
            </FormControl>
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/geographical-area"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </>
  );
}

export { GeographicalAction };
